import { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

/** YYYY-MM-DD in local time (so a date clicked on the picker matches the store's calendar day). */
const toISODate = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fmt = (d) => d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

/** Range picker that reports selected {from, to} as YYYY-MM-DD strings via onApply.
 *  Caps browsable range at 3 years back from today (we keep sales records for 3 years). */
export function DateRangePicker({ value, onApply, label = "Custom range" }) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(value || undefined);
  const today = new Date();
  const earliest = new Date(); earliest.setFullYear(earliest.getFullYear() - 3);

  const apply = () => {
    if (range?.from && range?.to) {
      onApply({ start: toISODate(range.from), end: toISODate(range.to) });
      setOpen(false);
    }
  };
  const summary = range?.from
    ? (range.to ? `${fmt(range.from)} – ${fmt(range.to)}` : fmt(range.from))
    : label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 px-3 h-8 rounded-full text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:text-neutral-900"
          data-testid="date-range-trigger"
        >
          <CalendarIcon className="w-3.5 h-3.5" />
          <span className="max-w-[220px] truncate">{summary}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0" data-testid="date-range-popover">
        <Calendar
          mode="range"
          selected={range}
          onSelect={setRange}
          numberOfMonths={2}
          disabled={{ before: earliest, after: today }}
          defaultMonth={range?.from || today}
          initialFocus
        />
        <div className="flex items-center justify-between px-3 py-2 border-t border-neutral-100">
          <p className="text-[11px] text-neutral-500">Up to 3 years back</p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setRange(undefined)} data-testid="date-range-clear">Clear</Button>
            <Button size="sm" onClick={apply} disabled={!range?.from || !range?.to} data-testid="date-range-apply">Apply</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
